import { toastError, toastSuccess } from "helpers";
import { createLogger } from "logging";
import { useTranslations } from "providers";
import { useMeeting } from "providers";
import { useApi } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { StandardMeetingsPanel } from "./components/StandardMeetingsPanel";
import { StandardMeetingsPanelContext, StandardMeetingsPanelStatus } from "./StandardMeetingsPanelContext";

const logger = createLogger("StandardMeetingsPanelProvider");

export const StandardMeetingsPanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { standardMeetingsApi } = useApi();
  const { meetingId, reloadMeeting } = useMeeting();

  const [isOpen, setIsOpen] = useState(false);
  const [templateId, setTemplateId] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showStandardMeetingsPanel = useCallback(() => {
    logger.debug("showStandardMeetingsPanel()");
    setIsOpen(true);
    setTemplateId(undefined);
    setIsSubmitting(false);
  }, []);

  const closeStandardMeetingsPanel = useCallback(() => {
    logger.debug("closeStandardMeetingsPanel()");
    setIsOpen(false);
    setTemplateId(undefined);
    setIsSubmitting(false);
  }, []);

  const handleSetTemplateId = useCallback((id: number | undefined) => {
    logger.debug("setTemplateId()", id);
    setTemplateId(id);
  }, []);

  const canSubmit = useCallback(() => {
    return !isSubmitting && !(templateId === undefined || templateId === 0);
  }, [isSubmitting, templateId]);

  const handleSubmit = useCallback(async () => {
    if (!meetingId || !canSubmit()) {
      logger.warn("Cannot submit - invalid state", { meetingId, templateId, isSubmitting });
      return;
    }

    logger.debug("handleSubmit()", { meetingId, templateId });
    setIsSubmitting(true);

    try {
      await standardMeetingsApi.apply(meetingId, templateId!);

      const message = i18n.StandardMeeting_Apply_Success;
      toastSuccess(message);

      closeStandardMeetingsPanel();
      reloadMeeting();
    } catch (error) {
      logger.error("Error applying standard meeting:", error);

      const message = i18n.StandardMeeting_Apply_Error.replace("{0}", String(error));
      toastError(message, error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    meetingId,
    templateId,
    isSubmitting,
    standardMeetingsApi,
    i18n,
    closeStandardMeetingsPanel,
    reloadMeeting,
    canSubmit,
  ]);

  const status: StandardMeetingsPanelStatus = isSubmitting ? "submitting" : isOpen ? "idle" : "closed";

  const contextValue = useMemo(
    () => ({
      status,
      isOpen,
      templateId,
      isSubmitting,
      showStandardMeetingsPanel,
      closeStandardMeetingsPanel,
      setTemplateId: handleSetTemplateId,
      handleSubmit,
      canSubmit,
    }),
    [
      status,
      isOpen,
      templateId,
      isSubmitting,
      showStandardMeetingsPanel,
      closeStandardMeetingsPanel,
      handleSetTemplateId,
      handleSubmit,
      canSubmit,
    ]
  );

  return (
    <StandardMeetingsPanelContext.Provider value={contextValue}>
      {children}
      <StandardMeetingsPanel />
    </StandardMeetingsPanelContext.Provider>
  );
};
